import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth } from '@core/services/s_auth';

/**
 * Waits for the permissionsResource to finish loading before evaluating access.
 * This prevents the race condition where permissions are empty because the
 * async resource hasn't resolved yet right after login.
 */
async function waitForPermissions(auth: Auth): Promise<void> {
  if (!auth.permissionsResource.isLoading()) return;

  return new Promise(resolve => {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (!auth.permissionsResource.isLoading() || attempts > 30) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
}

export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(Auth);
  const router = inject(Router);

  try {
    // 1. Ensure the user has a valid session with a profile
    const session = await auth.getCurrentUserWithProfile();

    if (!session) {
      sessionStorage.setItem('returnUrl', state.url);
      router.navigate(['/login']);
      return false;
    }

    // 2. Block backoffice access for basic 'user' role
    if (session.profile.role === 'user') {
      await auth.signOut();
      router.navigate(['/login']);
      return false;
    }

    // 3. Wait for permissions to load (avoids race condition after login)
    await waitForPermissions(auth);

    // 4. Ensure at least some permissions exist for this account
    //    If zero permissions after loading, the account is not configured
    const userPermissions = auth.allPermissions();
    if (userPermissions.length === 0) {
      console.warn('authGuard: No permissions found for role:', session.profile.role);
      // Allow access anyway — permissions may not be set up yet in DB.
      // Remove this line and uncomment the block below to enforce strict mode:
      return true;

      // Strict mode (uncomment to enforce):
      // router.navigate(['/login']);
      // return false;
    }

    // Access granted — fine-grained per-route checks happen at component level
    return true;
  } catch (error) {
    console.error('Error in authGuard:', error);
    router.navigate(['/login']);
    return false;
  }
};
